import { useEffect, useRef } from 'react';
import './style.css';

const GAP = 34;
const RAIO = 170;
const FORCA = 38;
const LINK = 58;
const COR = [205, 15, 232]; 

function BgBanner() {
    const canvasRef = useRef(null);
    useEffect(() => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        const section = canvas.parentElement;
        const reduzido = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        let pontos = [];
        let largura = 0;
        let altura = 0;
        let frame = null;
        let visivel = true;
        let tempo = 0;
        const mouse = { x: -9999, y: -9999, ativo: false, ultimoMov: 0 };

        const montarGrade = () => {
            const dpr = Math.min(window.devicePixelRatio || 1, 2);
            largura = section.offsetWidth;
            altura = section.offsetHeight;
            canvas.width = largura * dpr;
            canvas.height = altura * dpr;
            canvas.style.width = `${largura}px`;
            canvas.style.height = `${altura}px`;
            ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

            pontos = [];
            const cols = Math.ceil(largura / GAP) + 1;
            const linhas = Math.ceil(altura / GAP) + 1;
            const offX = (largura - (cols - 1) * GAP) / 2;
            const offY = (altura - (linhas - 1) * GAP) / 2;
            for (let i = 0; i < cols; i++) {
                for (let j = 0; j < linhas; j++) {
                    const x = offX + i * GAP;
                    const y = offY + j * GAP;
                    pontos.push({ bx: x, by: y, x, y, vx: 0, vy: 0, brilho: 0 });
                }
            }
        };

        const desenhar = () => {
            ctx.clearRect(0, 0, largura, altura);
            const ocioso = !mouse.ativo || performance.now() - mouse.ultimoMov > 4000;
            let alvoX = mouse.x;
            let alvoY = mouse.y;
            if (ocioso) {
                tempo += 0.004;
                alvoX = largura * (0.5 + Math.sin(tempo * 1.3) * 0.35);
                alvoY = altura * (0.5 + Math.cos(tempo) * 0.3);
            }

            for (const p of pontos) {
                const dx = p.x - alvoX;
                const dy = p.y - alvoY;
                const dist = Math.sqrt(dx * dx + dy * dy);
                let alvoBrilho = 0;

                if (dist < RAIO) {
                    const f = 1 - dist / RAIO;
                    const ang = Math.atan2(dy, dx);
                    p.vx += Math.cos(ang) * f * FORCA * 0.06;
                    p.vy += Math.sin(ang) * f * FORCA * 0.06;
                    alvoBrilho = f;
                }
                p.vx += (p.bx - p.x) * 0.06;
                p.vy += (p.by - p.y) * 0.06;
                p.vx *= 0.82;
                p.vy *= 0.82;
                p.x += p.vx;
                p.y += p.vy;
                p.brilho += (alvoBrilho - p.brilho) * 0.12;
            }

            ctx.lineWidth = 1;
            const acesos = pontos.filter((p) => p.brilho >= 0.05);
            for (let a = 0; a < acesos.length; a++) {
                const p = acesos[a];
                for (let b = a + 1; b < acesos.length; b++) {
                    const q = acesos[b];
                    const dx = p.x - q.x;
                    const dy = p.y - q.y;
                    const d = dx * dx + dy * dy;
                    if (d > LINK * LINK) continue;
                    const alpha = Math.min(p.brilho, q.brilho) * (1 - Math.sqrt(d) / LINK) * 0.9;
                    ctx.strokeStyle = `rgba(${COR[0]}, ${COR[1]}, ${COR[2]}, ${alpha})`;
                    ctx.beginPath();
                    ctx.moveTo(p.x, p.y);
                    ctx.lineTo(q.x, q.y);
                    ctx.stroke();
                }
            }

            for (const p of pontos) {
                const b = p.brilho;
                const r = 255 + (COR[0] - 255) * b;
                const g = 255 + (COR[1] - 255) * b;
                const bl = 255 + (COR[2] - 255) * b;
                ctx.fillStyle = `rgba(${r | 0}, ${g | 0}, ${bl | 0}, ${0.12 + b * 0.88})`;
                ctx.beginPath();
                ctx.arc(p.x, p.y, 1.1 + b * 1.6, 0, Math.PI * 2);
                ctx.fill();
            }
        };

        const loop = () => {
            desenhar();
            frame = visivel ? requestAnimationFrame(loop) : null;
        };

        const onMove = (e) => {
            const rect = section.getBoundingClientRect();
            const ponto = e.touches ? e.touches[0] : e;
            mouse.x = ponto.clientX - rect.left;
            mouse.y = ponto.clientY - rect.top;
            mouse.ativo = true;
            mouse.ultimoMov = performance.now();
        };
        const onLeave = () => { mouse.ativo = false; };

        let resizeTimer;
        const onResize = () => {
            clearTimeout(resizeTimer);
            resizeTimer = setTimeout(() => {
                montarGrade();
                if (reduzido) desenhar();
            }, 150);
        };

        montarGrade();

        if (reduzido) {
            desenhar(); // grade estática, sem animação
            window.addEventListener('resize', onResize);
            return () => window.removeEventListener('resize', onResize);
        }

        // pausa a animação quando o banner sai da tela
        const observer = new IntersectionObserver(([entry]) => {
            visivel = entry.isIntersecting;
            if (visivel && !frame) frame = requestAnimationFrame(loop);
        });
        observer.observe(section);

        section.addEventListener('mousemove', onMove);
        section.addEventListener('mouseleave', onLeave);
        section.addEventListener('touchmove', onMove, { passive: true });
        section.addEventListener('touchend', onLeave);
        window.addEventListener('resize', onResize);
        frame = requestAnimationFrame(loop);

        return () => {
            cancelAnimationFrame(frame);
            clearTimeout(resizeTimer);
            observer.disconnect();
            section.removeEventListener('mousemove', onMove);
            section.removeEventListener('mouseleave', onLeave);
            section.removeEventListener('touchmove', onMove);
            section.removeEventListener('touchend', onLeave);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    return <canvas ref={canvasRef} className="bg-banner-canvas" aria-hidden="true" />;
}

export default BgBanner;
